import {
  Button,
  FormControl,
  Input,
  Stack,
  Text,
  Grid,
  Select,
  useColorModeValue,
  Checkbox,
  Box,
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalBody,
  ModalFooter,
  useDisclosure,
} from "@chakra-ui/react";
import { validate as validateEmail } from "email-validator";
import React, { useState, useEffect } from "react";
import { useForm } from "react-hook-form";
import { useButtonProps } from "hooks";
import { FormErrorLabel } from "screens/panels/settings/tabs/subscription/SubscribeForm";
import { yupResolver } from "@hookform/resolvers/yup";
import * as yup from "yup";
import { useCopyValue } from "hooks/useCopies";

interface FormData {
  legalBusinessName: string;
  country: string;
  streetAddress: string;
  provinceState: string;
  postalZip: string;
  registrationTaxId: string;
  websiteUrl: string;
  crunchbasePitchbookUrl: string;
  linkedinFacebookUrl: string;
  fullName: string;
  emailAddress: string;
  phoneNumber: string;
  acceptTerms: boolean;
}

interface ClaimCompanyFormData {
  legalBusinessName: string;
  country: string;
  streetAddress: string;
  provinceState: string;
  postalZip: string;
  registrationTaxId: string;
  websiteUrl: string;
  crunchbasePitchbookUrl?: string;
  linkedinFacebookUrl?: string;
  fullName: string;
  emailAddress: string;
  phoneNumber: string;
  acceptTerms: boolean;
}

interface Props {
  onSubmit: (fields: ClaimCompanyFormData) => void;
  isVerifying?: boolean;
  submitError?: string;
  onFormChange?: (formData: Partial<ClaimCompanyFormData>) => void;
}

// Regular expression to check for HTML tags
const noHtmlTags = /<\/?[^>]+(>|$)/g;

// URL validation regex
const urlRegex = /^(https?:\/\/)?([\da-z.-]+)\.([a-z.]{2,6})([/\w .-]*)*\/?$/;

// Phone number validation regex (basic international format)
const phoneRegex = /^[+]?[1-9][\d]{0,15}$/;

const schema = yup.object({
  legalBusinessName: yup
    .string()
    .required("Legal business name is required")
    .min(2, "Business name must be at least 2 characters")
    .max(100, "Business name cannot exceed 100 characters")
    .test("no-html-tags", "Business name is not valid", (value) => !noHtmlTags.test(value || "")),
  country: yup.string().required("Country is required"),
  streetAddress: yup
    .string()
    .required("Street address is required")
    .min(5, "Street address must be at least 5 characters")
    .max(200, "Street address cannot exceed 200 characters")
    .test("no-html-tags", "Street address is not valid", (value) => !noHtmlTags.test(value || "")),
  provinceState: yup
    .string()
    .required("Province/State is required")
    .min(2, "Province/State must be at least 2 characters")
    .max(50, "Province/State cannot exceed 50 characters")
    .test("no-html-tags", "Province/State is not valid", (value) => !noHtmlTags.test(value || "")),
  postalZip: yup
    .string()
    .required("Postal/Zip code is required")
    .min(3, "Postal/Zip code must be at least 3 characters")
    .max(20, "Postal/Zip code cannot exceed 20 characters")
    .test("no-html-tags", "Postal/Zip code is not valid", (value) => !noHtmlTags.test(value || "")),
  registrationTaxId: yup
    .string()
    .required("Registration or Tax ID is required")
    .min(5, "Registration or Tax ID must be at least 5 characters")
    .max(50, "Registration or Tax ID cannot exceed 50 characters")
    .test("no-html-tags", "Registration or Tax ID is not valid", (value) => !noHtmlTags.test(value || "")),
  websiteUrl: yup
    .string()
    .required("Website URL is required")
    .test("url", "Please enter a valid URL", (value) => urlRegex.test(value || "")),
  crunchbasePitchbookUrl: yup
    .string()
    .default("")
    .test("url", "Please enter a valid URL", (value) => !value || urlRegex.test(value)),
  linkedinFacebookUrl: yup
    .string()
    .default("")
    .test("url", "Please enter a valid URL", (value) => !value || urlRegex.test(value)),
  fullName: yup
    .string()
    .required("Full name is required")
    .min(2, "Full name must be at least 2 characters")
    .max(100, "Full name cannot exceed 100 characters")
    .test("no-html-tags", "Full name is not valid", (value) => !noHtmlTags.test(value || "")),
  emailAddress: yup
    .string()
    .required("Email address is required")
    .email("Email must be a valid email address")
    .test("email", "Email must be a valid email address", (value) => validateEmail(value || "")),
  phoneNumber: yup
    .string()
    .required("Phone number is required")
    .test("phone", "Please enter a valid phone number", (value) => phoneRegex.test(value || "")),
  acceptTerms: yup.boolean().required().oneOf([true], "You must accept the terms and conditions"),
});

// Common countries list
const countries = [
  "United States",
  "Canada",
  "United Kingdom",
  "Australia",
  "Germany",
  "France",
  "Japan",
  "Singapore",
  "Netherlands",
  "Switzerland",
  "Sweden",
  "Norway",
  "Denmark",
  "Finland",
  "New Zealand",
  "Ireland",
  "Belgium",
  "Austria",
  "Luxembourg",
  "Other",
];

export const ClaimCompanyForm = ({ onSubmit, isVerifying, submitError, onFormChange }: Props) => {
  const [localSubmitError, setLocalSubmitError] = useState(submitError);
  const [formDataToSubmit, setFormDataToSubmit] = useState<ClaimCompanyFormData | null>(null);
  const { isOpen, onOpen, onClose } = useDisclosure();

  // Color mode values
  const inputBgColor = useColorModeValue("white", "gray.700");
  const helperTextColor = useColorModeValue("gray.500", "gray.400");
  const buttonHoverBgColor = useColorModeValue("#81c34c", "#6ba33f");
  const buttonHoverBorderColor = useColorModeValue("#81c34c", "#6ba33f");
  const sectionTitleColor = useColorModeValue("gray.800", "gray.200");
  const registrationSubheadingBgColor = useColorModeValue("gray.100", "gray.800");
  const modalBgColor = useColorModeValue("white", "gray.800");
  const registrationSubheading = useCopyValue("copy_registration_form_subheading_claim_company");
  const confirmationText = useCopyValue("copy_registration_confirm_claim_company");

  const {
    register,
    handleSubmit,
    formState: { errors },
    resetField,
    watch,
  } = useForm<FormData>({
    resolver: yupResolver(schema),
  });
  const commonButtonProps = useButtonProps("md", "cta");

  // Watch all form values for changes
  const watchedValues = watch();

  // Track form changes and update step completion status
  useEffect(() => {
    if (onFormChange) {
      onFormChange(watchedValues);
    }
  }, [watchedValues, onFormChange]);

  // Handle modal confirmation
  const handleConfirmSubmit = () => {
    if (formDataToSubmit) {
      onSubmit(formDataToSubmit);
      setFormDataToSubmit(null);
    }
    onClose();
  };

  // Handle modal cancel
  const handleCancelSubmit = () => {
    setFormDataToSubmit(null);
    onClose();
  };

  return (
    <Stack width="100%" spacing={8} zIndex={2}>
      <form
        onSubmit={(e) => {
          if (isVerifying) {
            e.preventDefault();
            return;
          }
          handleSubmit((data) => {
            setLocalSubmitError("");
            // Convert empty strings to undefined for optional fields
            const formData: ClaimCompanyFormData = {
              ...data,
              crunchbasePitchbookUrl: data.crunchbasePitchbookUrl || undefined,
              linkedinFacebookUrl: data.linkedinFacebookUrl || undefined,
            };
            // Store form data and show confirmation modal
            setFormDataToSubmit(formData);
            onOpen();
          })(e);
        }}
        noValidate>
        {registrationSubheading && (
          <Box bgColor={registrationSubheadingBgColor} p={4} borderRadius="md" mb="1rem">
            <Text fontSize="sm" color={sectionTitleColor}>
              {registrationSubheading}
            </Text>
          </Box>
        )}
        <Stack spacing={8}>
          {/* Business Information */}
          <Stack spacing={4}>
            <Text fontSize="lg" fontWeight="semibold" color={sectionTitleColor}>
              Business Information
            </Text>
            <FormControl isInvalid={!!errors.legalBusinessName}>
              <Input
                {...register("legalBusinessName")}
                id="claim-legal-business-name"
                type="text"
                placeholder="Legal Business Name"
                size="md"
                bg={inputBgColor}
                onFocus={() => errors.legalBusinessName && resetField("legalBusinessName")}
              />
              {errors.legalBusinessName?.message && <FormErrorLabel error={errors.legalBusinessName.message} />}
            </FormControl>
          </Stack>

          {/* Business Location */}
          <Stack spacing={4}>
            <Text fontSize="lg" fontWeight="semibold" color={sectionTitleColor}>
              Business Location
            </Text>
            <FormControl isInvalid={!!errors.country}>
              <Select
                {...register("country")}
                id="claim-country"
                placeholder="Country drop down list ..."
                size="md"
                bg={inputBgColor}
                onFocus={() => errors.country && resetField("country")}>
                {countries.map((country) => (
                  <option key={country} value={country}>
                    {country}
                  </option>
                ))}
              </Select>
              {errors.country?.message && <FormErrorLabel error={errors.country.message} />}
            </FormControl>
          </Stack>

          {/* Business Address */}
          <Stack spacing={4}>
            <Text fontSize="lg" fontWeight="semibold" color={sectionTitleColor}>
              Business Address
            </Text>
            <FormControl isInvalid={!!errors.streetAddress}>
              <Input
                {...register("streetAddress")}
                id="claim-street-address"
                type="text"
                placeholder="Street Address"
                size="md"
                bg={inputBgColor}
                onFocus={() => errors.streetAddress && resetField("streetAddress")}
              />
              {errors.streetAddress?.message && <FormErrorLabel error={errors.streetAddress.message} />}
            </FormControl>
            <Grid templateColumns="repeat(2, 1fr)" gap={4}>
              <FormControl isInvalid={!!errors.provinceState}>
                <Input
                  {...register("provinceState")}
                  id="claim-province-state"
                  type="text"
                  placeholder="Province/State"
                  size="md"
                  bg={inputBgColor}
                  onFocus={() => errors.provinceState && resetField("provinceState")}
                />
                {errors.provinceState?.message && <FormErrorLabel error={errors.provinceState.message} />}
              </FormControl>
              <FormControl isInvalid={!!errors.postalZip}>
                <Input
                  {...register("postalZip")}
                  id="claim-postal-zip"
                  type="text"
                  placeholder="Postal/Zip"
                  size="md"
                  bg={inputBgColor}
                  onFocus={() => errors.postalZip && resetField("postalZip")}
                />
                {errors.postalZip?.message && <FormErrorLabel error={errors.postalZip.message} />}
              </FormControl>
            </Grid>
          </Stack>

          {/* Business Registration or Tax ID */}
          <Stack spacing={4}>
            <Text fontSize="lg" fontWeight="semibold" color={sectionTitleColor}>
              Business Registration or Tax ID
            </Text>
            <FormControl isInvalid={!!errors.registrationTaxId}>
              <Input
                {...register("registrationTaxId")}
                id="claim-registration-tax-id"
                type="text"
                placeholder="Registration or Tax ID or EIN"
                size="md"
                bg={inputBgColor}
                onFocus={() => errors.registrationTaxId && resetField("registrationTaxId")}
              />
              {errors.registrationTaxId?.message && <FormErrorLabel error={errors.registrationTaxId.message} />}
            </FormControl>
          </Stack>

          {/* Business Online Details */}
          <Stack spacing={4}>
            <Text fontSize="lg" fontWeight="semibold" color={sectionTitleColor}>
              Business Online Details
            </Text>
            <FormControl isInvalid={!!errors.websiteUrl}>
              <Input
                {...register("websiteUrl")}
                id="claim-website-url"
                type="url"
                placeholder="Website URL"
                size="md"
                bg={inputBgColor}
                onFocus={() => errors.websiteUrl && resetField("websiteUrl")}
              />
              {errors.websiteUrl?.message && <FormErrorLabel error={errors.websiteUrl.message} />}
            </FormControl>
            <FormControl isInvalid={!!errors.crunchbasePitchbookUrl}>
              <Input
                {...register("crunchbasePitchbookUrl")}
                id="claim-crunchbase-pitchbook-url"
                type="url"
                placeholder="Crunchbase or Pitchbook URL"
                size="md"
                bg={inputBgColor}
                onFocus={() => errors.crunchbasePitchbookUrl && resetField("crunchbasePitchbookUrl")}
              />
              {errors.crunchbasePitchbookUrl?.message && <FormErrorLabel error={errors.crunchbasePitchbookUrl.message} />}
            </FormControl>
            <FormControl isInvalid={!!errors.linkedinFacebookUrl}>
              <Input
                {...register("linkedinFacebookUrl")}
                id="claim-linkedin-facebook-url"
                type="url"
                placeholder="LinkedIn or Facebook URL"
                size="md"
                bg={inputBgColor}
                onFocus={() => errors.linkedinFacebookUrl && resetField("linkedinFacebookUrl")}
              />
              {errors.linkedinFacebookUrl?.message && <FormErrorLabel error={errors.linkedinFacebookUrl.message} />}
            </FormControl>
          </Stack>

          {/* Primary Contact Information */}
          <Stack spacing={4}>
            <Text fontSize="lg" fontWeight="semibold" color={sectionTitleColor}>
              Primary Contact Information
            </Text>
            <FormControl isInvalid={!!errors.fullName}>
              <Input
                {...register("fullName")}
                id="claim-full-name"
                type="text"
                placeholder="Full Name"
                size="md"
                bg={inputBgColor}
                onFocus={() => errors.fullName && resetField("fullName")}
              />
              {errors.fullName?.message && <FormErrorLabel error={errors.fullName.message} />}
            </FormControl>
            <FormControl isInvalid={!!errors.emailAddress}>
              <Input
                {...register("emailAddress")}
                id="claim-email-address"
                type="email"
                placeholder="Email Address"
                size="md"
                bg={inputBgColor}
                onFocus={() => errors.emailAddress && resetField("emailAddress")}
              />
              {errors.emailAddress?.message && <FormErrorLabel error={errors.emailAddress.message} />}
            </FormControl>
            <FormControl isInvalid={!!errors.phoneNumber}>
              <Input
                {...register("phoneNumber")}
                id="claim-phone-number"
                type="tel"
                placeholder="Phone Number"
                size="md"
                bg={inputBgColor}
                onFocus={() => errors.phoneNumber && resetField("phoneNumber")}
              />
              {errors.phoneNumber?.message && <FormErrorLabel error={errors.phoneNumber.message} />}
            </FormControl>
          </Stack>

          {/* Terms and Conditions */}
          <FormControl isInvalid={!!errors.acceptTerms}>
            <Checkbox {...register("acceptTerms")} id="claim-accept-terms" colorScheme="green" size="sm">
              <Text fontSize="sm" color={helperTextColor}>
                Confirm that all the business details are correct and accept the terms and conditions.
              </Text>
            </Checkbox>
            {errors.acceptTerms?.message && <FormErrorLabel error={errors.acceptTerms.message} />}
          </FormControl>

          {/* Submit Error */}
          {localSubmitError && <FormErrorLabel error={localSubmitError} />}

          {/* Submit Button */}
          <Button
            {...commonButtonProps}
            _hover={{ bgColor: buttonHoverBgColor, borderColor: buttonHoverBorderColor }}
            type="submit"
            width="20rem"
            mx="auto"
            size="lg"
            borderRadius="full"
            id="claim-company-submit"
            disabled={isVerifying}
            borderColor={isVerifying ? "#81c34c" : "primary.default"}
            bgColor={isVerifying ? "#81c34c" : "primary.default"}
            opacity={isVerifying ? 0.6 : 1}
            cursor={isVerifying ? "default" : "pointer"}>
            {isVerifying ? (
              <Text as="span" position="relative">
                VERIFYING BUSINESS
                <Text
                  as="span"
                  sx={{
                    "@keyframes ellipsis": {
                      "0%": { content: "''" },
                      "25%": { content: "'.'" },
                      "50%": { content: "'..'" },
                      "75%": { content: "'...'" },
                    },
                    "&::after": {
                      content: "''",
                      animation: "ellipsis 1.2s steps(4, end) infinite",
                      position: "absolute",
                      display: "inline-block",
                      marginLeft: "2px",
                      width: "12px",
                    },
                  }}></Text>
              </Text>
            ) : (
              "Verify Business"
            )}
          </Button>
        </Stack>
      </form>

      {/* Confirmation Modal */}
      <Modal isOpen={isOpen} onClose={handleCancelSubmit} isCentered>
        <ModalOverlay />
        <ModalContent bg={modalBgColor} maxW="400px">
          <ModalHeader color={sectionTitleColor} fontSize="xl" fontWeight="semibold">
            Confirm Claim {formDataToSubmit?.legalBusinessName ? `of ${formDataToSubmit.legalBusinessName}` : ""}
          </ModalHeader>
          <ModalBody>
            <Text color={helperTextColor} fontSize="sm" lineHeight="1.5">
              {confirmationText}
            </Text>
          </ModalBody>
          <ModalFooter gap={3}>
            <Button variant="outline" onClick={handleCancelSubmit} size="md" borderRadius="full" px={6}>
              Cancel
            </Button>
            <Button
              {...commonButtonProps}
              onClick={handleConfirmSubmit}
              size="md"
              borderRadius="full"
              px={6}
              _hover={{ bgColor: buttonHoverBgColor, borderColor: buttonHoverBorderColor }}>
              Confirm
            </Button>
          </ModalFooter>
        </ModalContent>
      </Modal>
    </Stack>
  );
};
